// @ts-strict-ignore
import { groupBy, orderBy, sortBy } from 'lodash';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { DataMap } from 'mobx/stores/DataMap';

import CareManagementFetcher from 'fetchers/CareManagementFetcher';

import { ConstantsFetcher } from 'fetchers/ConstantsFetcher';

import { CmStatusResponseType } from 'fetchers/responses/care-management.response';

import { TagCreateAttributes, TagsFetcher, TagUpdateAttributes } from 'fetchers/TagsFetcher';

import { FEATURES } from 'constants/features';

import { ALL_ROLES, NO_ROLE } from 'constants/tasks.const';

import { ClinicianCredential } from 'models/ClinicianCredential';
import { DenyCallbackReason } from 'models/DenyCallbackReason';
import {
  DrugSpecific,
  DrugSpecificProtocol,
  DrugSpecificReportProtocolTemplate,
  DrugType
} from 'models/DrugSpecific';
import { CmEpisodeTypes, EpisodeRuleset, EpisodeTypes } from 'models/Episode';

import { IIcdCategory, IIcdCode } from 'models/Icd';
import MissedDosesReasons from 'models/MissedDosesReasons';
import { PatientEpisodeEndReason } from 'models/PatientEpisodeEndReason';
import PatientTag from 'models/PatientTag';
import RegimenTemplate from 'models/RegimenTemplate';
import { SelectOption } from 'models/SelectOption';
import { SymptomType } from 'models/TicketTypes';

import { specificDrugTypeToLabelMap } from 'views/Widgets/DrugSpecificProtocolFields.constants';

import { ISelectOption, sortOptionsByLocale } from 'views/Widgets/StyledSelect';

import { RootStore } from './rootStore';

type ServerMetadata<T> = {
  id: number;
  isDeleted?: boolean;
  name: T;
};

export type EpisodeMetadata = {
  createdAt: string;
  duration: number;
  ruleset: EpisodeRuleset;
  type: EpisodeTypes | CmEpisodeTypes;
  id: number;
  isDeleted: boolean;
  name: string;
  updatedAt: string;
};

export interface RoleMetadata {
  isSystem: boolean;
  hierarchyLevel: number;
}

export interface InstitutionRole {
  id: number;
  name: string;
  metadata: RoleMetadata;
}

export type TaskRoleOption = ServerMetadata<string>;

export interface RemainingDosesOption {
  id: RemainingDoseOption;
  text: string;
}

export enum RemainingDoseOption {
  ImOut = 'ImOut',
  DayOrTwo = 'DayOrTwo',
  AboutAWeek = 'AboutAWeek',
  OverAWeek = 'OverAWeek'
}

export interface InstitutionMetadata {
  distressCauses: SymptomType[];
  taskRoles: TaskRoleOption[];
  episodes: EpisodeMetadata[];
  episodeEndReasons: PatientEpisodeEndReason[];
  patientTags: PatientTag[];
  missedDosesReasons: MissedDosesReasons[];
  denyCallbackReasons: DenyCallbackReason[];
  credentials: ClinicianCredential[];
  remainingDoseOptions: RemainingDosesOption[];
  institutionRoles: InstitutionRole[];
}

export const ALL_ROLES_OPTION = {
  label: 'All Roles',
  value: { id: ALL_ROLES, name: 'All Roles' }
};

export const NO_ROLE_OPTION = {
  label: 'No Role',
  value: { id: NO_ROLE, name: 'No Role' }
};

export const DECEASED_TAG_ID = -1;

export const sortPatientTags = (tags: PatientTag[]) => {
  return orderBy(
    tags,
    [(tag: PatientTag) => tag.priority || 0, (tag: PatientTag) => tag.name.toLowerCase()],
    ['desc', 'asc']
  );
};

export default class ConstantsStore {
  @observable
  yesNoQuestions: Array<{ id: number; questionText: string }>;

  @observable
  icdCategories: IIcdCategory[];

  @observable
  episodeTemplatesMetadataMap: DataMap<EpisodeMetadata> = new DataMap<EpisodeMetadata>();

  @observable
  taskRoles: DataMap<TaskRoleOption> = new DataMap<TaskRoleOption>();

  @observable
  denyCallbackReasons: DataMap<DenyCallbackReason> = new DataMap<DenyCallbackReason>();

  @observable
  tags: DataMap<PatientTag> = new DataMap<PatientTag>();

  @observable
  regimens: DataMap<RegimenTemplate> = new DataMap<RegimenTemplate>();

  @observable
  specificDrugs: DataMap<DrugSpecific> = new DataMap<DrugSpecific>();

  @observable
  initialConstantsLoaded: boolean = false;

  @observable
  cmStatuses = new DataMap<CmStatusResponseType>();

  @observable
  clinicianCredential: DataMap<ClinicianCredential> = new DataMap<ClinicianCredential>();

  @observable
  symptomsMap = new DataMap<SymptomType>();

  @observable
  missedDosesReasonsMap = new DataMap<MissedDosesReasons>();

  @observable
  remainingDoseOptionsMap = new DataMap<RemainingDosesOption>();

  @observable
  institutionRoles = new DataMap<InstitutionRole>();

  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
  }

  @action
  async systemLoadInit() {
    await this.rootStore.stores.settingsStore.init();
    const regionsRequest = this.rootStore.stores.settingsStore.hasFeature(FEATURES.REGIONS)
      ? this.rootStore.stores.locationsStore.fetchRegions()
      : Promise.resolve();

    await Promise.all([
      // must calls we need for before any view loads
      this.rootStore.stores.departmentsStore.fetchDepartments(),
      this.rootStore.stores.cliniciansStore.fetchAssigneesForFilterSelect(),
      this.fetchInstitutionMetadata(),
      this.fetchRegimenTemplates(),
      this.rootStore.stores.ticketTypesStore.fetchOperatorTicketTypes(),
      this.rootStore.stores.pathwaysStore.fetchPathwayBasicInfo(),
      this.rootStore.stores.locationsStore.fetchPatientLocations(),
      this.rootStore.stores.providersStore.fetchPatientProviders(),
      regionsRequest
    ]);
    runInAction(() => {
      this.initialConstantsLoaded = true;
    });
  }

  @action
  clear() {
    this.initialConstantsLoaded = false;
  }

  searchIcdCodeCategories(searchValue: string = '') {
    return ConstantsFetcher.getIcdCategories(searchValue);
  }

  @action
  async fetchCareManagementFilterData() {
    const response = await CareManagementFetcher.fetchCareManagementFilterData();

    runInAction(() => {
      this.cmStatuses.setItems(response.cmStatuses);
    });

    return response;
  }

  getIcdCodeById(codeId: number): IIcdCode {
    let code = null;
    this.icdCategories.some((category) => {
      code = category.icdCodes.find((icdCode) => icdCode.id === codeId);
      return !!code;
    });
    return code;
  }

  getEpisodeById(episodeId: number): EpisodeMetadata {
    return this.episodeTemplatesMetadataMap.get(episodeId);
  }

  getDenyCallbackReasonById(reasonId: number): DenyCallbackReason {
    return this.denyCallbackReasons.get(reasonId);
  }

  getRoleByIdWithAllAndNoRoles(roleId: number | null) {
    switch (roleId) {
      case ALL_ROLES:
        return { id: ALL_ROLES, name: ALL_ROLES_OPTION.label };
      case NO_ROLE:
        return { id: NO_ROLE, name: NO_ROLE_OPTION.label };
      default:
        return this.taskRoles.get(roleId);
    }
  }

  getCauseNameById = (causeId: number) => {
    return this.symptomsMap.get(causeId)?.name || '';
  };

  @computed
  get specificDrugsForSelect() {
    return this.specificDrugs.items.map((drug) => ({ label: drug.name, value: drug.id }));
  }

  async fetchRegimenTemplates() {
    const { regimens, drugs } = await ConstantsFetcher.fetchRegimenAndDrugTemplates();

    runInAction(() => {
      this.regimens.setItems(regimens);
      this.specificDrugs.setItems(drugs);
    });
  }

  getSpecificDrugById(specificDrugId: number): DrugSpecific {
    return this.specificDrugs.get(specificDrugId);
  }

  getSpecificDrugTypes(specificDrugId: number) {
    const drug = this.getSpecificDrugById(specificDrugId);

    if (drug) {
      return drug.reportProtocolTemplates;
    }

    return [];
  }

  getSpecificDrugTypeId(specificDrugId: number, type: DrugSpecificProtocol) {
    const drugTypes = this.getSpecificDrugTypes(specificDrugId);
    if (type === DrugSpecificProtocol.SpecificTimesHourlyWithoutHospitalization) {
      return drugTypes.find(
        (drugType) =>
          drugType.type === DrugSpecificProtocol.Hourly &&
          drugType.reportTimes &&
          !drugType.withHospitalization
      )?.id;
    }
    if (type === DrugSpecificProtocol.SpecificTimesHourlyWithHospitalization) {
      return drugTypes.find(
        (drugType) =>
          drugType.type === DrugSpecificProtocol.Hourly &&
          drugType.reportTimes &&
          drugType.withHospitalization
      )?.id;
    }
    return drugTypes.find((drugType) => drugType.type === type && !drugType.reportTimes)?.id;
  }

  getSpecificDrugTypesForSelect(specificDrugId: number): SelectOption<number>[] {
    const drug = this.getSpecificDrugById(specificDrugId);

    if (drug) {
      return drug.reportProtocolTemplates.map((template) => {
        if (template.reportTimes) {
          return {
            label: template.withHospitalization
              ? specificDrugTypeToLabelMap[
                  DrugSpecificProtocol.SpecificTimesHourlyWithHospitalization
                ]
              : specificDrugTypeToLabelMap[
                  DrugSpecificProtocol.SpecificTimesHourlyWithoutHospitalization
                ],
            value: template.id
          };
        }
        if (
          drug.drugType === DrugType.Bispecific &&
          template.type === DrugSpecificProtocol.WeeklyInfection
        ) {
          return {
            label: specificDrugTypeToLabelMap[DrugSpecificProtocol.WeeklyInfectionBiSpecific],
            value: template.id
          };
        }
        return {
          label: specificDrugTypeToLabelMap[template.type],
          value: template.id
        };
      });
    }

    return [];
  }

  getSpecificDrugTemplate(
    specificDrugId: number,
    typeId: number
  ): DrugSpecificReportProtocolTemplate {
    const drug = this.getSpecificDrugById(specificDrugId);
    return drug?.reportProtocolTemplates?.find((type) => type.id === typeId);
  }

  getYesNoQuestions() {
    return ConstantsFetcher.getYesNoQuestions().then((questions) => {
      runInAction(() => {
        this.yesNoQuestions = questions;
      });
    });
  }

  @computed
  get taskRolesForSelect(): ISelectOption<TaskRoleOption>[] {
    const rolesOptions = this.taskRoles.items
      .filter((option) => !option.isDeleted)
      .map((role: TaskRoleOption) => ({
        value: role,
        label: role.name
      }))
      .sort(sortOptionsByLocale);

    return [NO_ROLE_OPTION, ALL_ROLES_OPTION, ...rolesOptions];
  }

  getRolesForSelect(): ISelectOption<void>[] {
    const grouped = groupBy(this.institutionRoles.items, (role) =>
      role.metadata.isSystem ? 'isSystem' : 'nonSystem'
    );

    return [
      {
        label: '',
        options: sortBy(grouped.isSystem, (role) => role.name).map((role) => ({
          value: role.id,
          label: role.name
        }))
      },
      {
        label: 'Custom Roles',
        options: sortBy(grouped.nonSystem, (role) => role.name).map((role) => ({
          value: role.id,
          label: role.name
        }))
      }
    ];
  }

  async fetchInstitutionMetadata() {
    const institutionMetadata = await ConstantsFetcher.getInstitutionMetadata();
    const {
      taskRoles,
      episodes,
      episodeEndReasons,
      patientTags,
      distressCauses,
      missedDosesReasons,
      denyCallbackReasons,
      credentials,
      remainingDoseOptions,
      institutionRoles
    } = institutionMetadata;

    runInAction(() => {
      this.episodeTemplatesMetadataMap.setItems(episodes);
      this.taskRoles.setItems(taskRoles);
      this.denyCallbackReasons.setItems(denyCallbackReasons);
      episodeEndReasons &&
        this.rootStore.stores.patientEpisodesStore.episodeEndReasons.setItems(episodeEndReasons);
      this.tags.setItems(patientTags);
      this.clinicianCredential.setItems(credentials);
      this.symptomsMap.setItems(distressCauses);
      this.missedDosesReasonsMap.setItems(missedDosesReasons);
      this.remainingDoseOptionsMap.setItems(remainingDoseOptions);
      this.institutionRoles.setItems(institutionRoles);
    });
  }

  getClinicianCredentialById(credentialId?: number) {
    if (!credentialId) {
      return null;
    }

    return this.clinicianCredential.get(credentialId);
  }

  @computed
  get regimenTemplatesForSelect() {
    return this.regimens.items
      .filter((regimen) => regimen.isActive)
      .map(({ id, name }) => ({
        value: id,
        label: name
      }));
  }

  async createTag(tag: TagCreateAttributes) {
    const tagId = await TagsFetcher.createTag(tag);
    this.tags.set(tagId, {
      id: tagId,
      institutionId: this.rootStore.stores.departmentsStore.rootDepartmentId,
      isEditable: true,
      ...tag
    });
  }

  async updateTag(tag: TagUpdateAttributes) {
    await TagsFetcher.updateTag(tag);
    this.tags.set(tag.id, { isEditable: true, ...tag });
  }

  async deleteTag(tagId: number) {
    await TagsFetcher.deleteTag(tagId);
    this.tags.delete(tagId);
  }

  @computed
  get activeTags() {
    return this.tags.items.filter((tag) => !tag.isDeleted);
  }

  @computed
  get activeTagsForSelect() {
    return sortPatientTags(this.activeTags).map((tag) => ({
      value: tag.id,
      label: tag.name
    }));
  }

  getTagsByIds = (tagIds: number[]) => {
    return tagIds.map((id) => this.tags.get(id)).filter((tag) => Boolean(tag));
  };

  getSymptomsByPathwayId(pathwayId: string) {
    return this.symptomsMap.items.filter((symptom) => symptom.pathwayId === pathwayId);
  }

  getRemainingDoseOptionById = (id: RemainingDoseOption): RemainingDosesOption => {
    return this.remainingDoseOptionsMap.get(id);
  };

  getSymptomOptionsForPathwayById = (pathwayId: string): SelectOption<number>[] =>
    this.getSymptomsByPathwayId(pathwayId).map((symptomItem) => ({
      label: symptomItem.name,
      value: symptomItem.id
    }));

  getRoleById = (id: number) => {
    return this.institutionRoles.get(id);
  };
}
